import React, { ComponentProps } from 'react';

import { Sidebar } from '../sidebar';

import { cn } from '@/lib/utils';

export interface FullLayoutLeftSidebarProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  open: boolean;
  widthPx: number;
  onResizeStart: (e: React.MouseEvent) => void;
  onResizeKeyDown: (e: React.KeyboardEvent) => void;
  ariaValuenow: number;
  ariaValuemin: number;
  ariaValuemax: number;
  sidebarProps: ComponentProps<typeof Sidebar>;
}

const FullLayoutLeftSidebar = React.forwardRef<HTMLDivElement, FullLayoutLeftSidebarProps>(
  (
    {
      open,
      widthPx,
      onResizeStart,
      onResizeKeyDown,
      ariaValuenow,
      ariaValuemin,
      ariaValuemax,
      sidebarProps,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        id="left-sidebar-pane"
        data-slot="full-layout-left-sidebar"
        tabIndex={-1}
        data-state={open ? 'open' : 'closed'}
        className={cn(
          'min-w-0 overflow-hidden flex flex-col relative transition-[width] data-[state=closed]:w-0 data-[state=closed]:pointer-events-none data-[state=open]:w-full md:data-[state=open]:w-[var(--left-nav-width)]',
          className,
        )}
        style={{
          ['--left-nav-width' as string]: `${widthPx}px`,
        }}
        {...props}
      >
        <Sidebar
          {...sidebarProps}
          collapsed={!open}
          className="transition-[width] w-[var(--left-nav-width)] h-[calc(100dvh-var(--header-nav-height,0))]"
        />

        {/* Window Splitter per APG: https://www.w3.org/ARIA/apg/patterns/windowsplitter/ */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- focusable separator per APG Window Splitter pattern */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={ariaValuenow}
          aria-valuemin={ariaValuemin}
          aria-valuemax={ariaValuemax}
          aria-label="Resize left sidebar"
          aria-controls="left-sidebar-pane"
          data-slot="full-layout-left-sidebar-resize-handle"
          /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focusable separator per APG Window Splitter pattern */
          tabIndex={0}
          onMouseDown={onResizeStart}
          onKeyDown={onResizeKeyDown}
          className="hidden lg:block absolute top-0 right-0 bottom-0 w-1 bg-transparent hover:bg-blue-500 cursor-ew-resize shrink-0 [&>div]:pointer-events-none"
        >
          <div className="absolute inset-y-0 -right-1 w-3" />
        </div>
      </div>
    );
  },
);

FullLayoutLeftSidebar.displayName = 'FullLayoutLeftSidebar';

export { FullLayoutLeftSidebar };
